import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Character } from "@/mocks/characters";
import { supabase, rowToCharacter, CharacterRow } from "@/lib/supabase";

const GPS_STATUS_KEY = "charamap_gps_granted";
const GPS_AREA_KEY = "charamap_gps_area";

function getGpsInfo(): { granted: boolean; area: string } {
  try {
    const status = localStorage.getItem(GPS_STATUS_KEY);
    const area = localStorage.getItem(GPS_AREA_KEY) ?? "";
    return { granted: status === "granted", area };
  } catch {
    return { granted: false, area: "" };
  }
}

const ProfilePage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [character, setCharacter] = useState<Character | null>(null);
  const [currentImg, setCurrentImg] = useState(0);
  const [related, setRelated] = useState<Character[]>([]);
  const [gpsGranted, setGpsGranted] = useState(false);
  const [gpsArea, setGpsArea] = useState("");

  useEffect(() => {
    if (!id) return;
    setCurrentImg(0);

    supabase.from("characters").select("*").eq("id", id).maybeSingle().then(({ data }) => {
      setCharacter(data ? rowToCharacter(data as CharacterRow) as Character : null);
    });

    supabase.from("characters").select("*").neq("id", id).eq("is_active", true).limit(3).then(({ data }) => {
      if (data) setRelated((data as CharacterRow[]).map(rowToCharacter) as Character[]);
    });

    const gpsInfo = getGpsInfo();
    setGpsGranted(gpsInfo.granted);
    setGpsArea(gpsInfo.area);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  const handleRelatedClick = useCallback((charId: string) => {
    navigate(`/profile/${charId}`);
  }, [navigate]);

  if (!character) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center text-gray-400">
          <i className="ri-user-search-line text-5xl mb-3 block"></i>
          <p>女の子が見つかりませんでした</p>
          <Link to="/characters" className="mt-4 inline-block text-pink-500 hover:underline cursor-pointer">
            一覧に戻る
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Nunito', sans-serif" }}>
      <Navbar />
      <div className="pt-16">
        <div className="max-w-4xl mx-auto px-3 md:px-6 py-5 md:py-10">
          {/* Back */}
          <Link
            to="/characters"
            className="inline-flex items-center gap-1 text-sm text-pink-400 hover:text-pink-600 mb-4 md:mb-6 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>女の子一覧に戻る
          </Link>

          {/* Main layout — stacked on mobile, side-by-side on md+ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">

            {/* ===== Images ===== */}
            <div>
              {/* Main image */}
              <div
                className="relative w-full rounded-2xl overflow-hidden bg-pink-50"
                style={{ height: "clamp(300px, 80vw, 420px)" }}
              >
                <img
                  src={character.images[currentImg]}
                  alt={character.name}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />

                {/* Nav arrows */}
                {character.images.length > 1 && (
                  <>
                    <button
                      onClick={() =>
                        setCurrentImg(
                          (prev) => (prev - 1 + character.images.length) % character.images.length
                        )
                      }
                      className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 w-8 h-8 md:w-9 md:h-9 flex items-center justify-center bg-white/80 rounded-full text-pink-500 hover:bg-white transition-all cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line text-lg"></i>
                    </button>
                    <button
                      onClick={() =>
                        setCurrentImg((prev) => (prev + 1) % character.images.length)
                      }
                      className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 w-8 h-8 md:w-9 md:h-9 flex items-center justify-center bg-white/80 rounded-full text-pink-500 hover:bg-white transition-all cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line text-lg"></i>
                    </button>
                  </>
                )}

                {/* Dots */}
                {character.images.length > 1 && (
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {character.images.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentImg(i)}
                        className={`h-1.5 rounded-full transition-all cursor-pointer ${
                          i === currentImg ? "bg-white w-4" : "bg-white/60 w-1.5"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              {character.images.length > 1 && (
                <div className="flex gap-2 mt-2.5 overflow-x-auto pb-1">
                  {character.images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentImg(i)}
                      className={`w-14 h-14 md:w-16 md:h-16 rounded-xl overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0 ${
                        i === currentImg ? "border-pink-400" : "border-transparent opacity-60"
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover object-top" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ===== Info ===== */}
            <div>
              {/* Name & type */}
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-800">{character.name}</h1>
                  <p className="text-pink-400 font-medium text-sm md:text-base">
                    {character.age != null ? `${character.age}歳` : "ひみつ"}
                  </p>
                </div>
                <span className="bg-pink-100 text-pink-500 text-xs md:text-sm px-2.5 py-1 rounded-full font-medium flex-shrink-0 ml-2">
                  {character.type}
                </span>
              </div>

              <div className="flex items-center gap-2 mb-3 md:mb-4">
                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                <span className="text-sm text-green-500">オンライン</span>
              </div>

              {/* Catchphrase */}
              <div className="bg-pink-50 rounded-2xl p-3 md:p-4 mb-4 md:mb-5 border-l-4 border-pink-400">
                <p className="text-pink-600 font-medium text-sm italic">
                  &ldquo;{character.catchphrase}&rdquo;
                </p>
              </div>

              {/* Profile Details grid */}
              <div className="grid grid-cols-2 gap-2 md:gap-3 mb-4 md:mb-5">
                {[
                  ...(character.height != null
                    ? [{ icon: "ri-ruler-line", label: "身長", value: `${character.height}cm` }]
                    : []),
                  ...(character.type ? [{ icon: "ri-heart-3-line", label: "タイプ", value: character.type }] : []),
                  ...(character.hobby ? [{ icon: "ri-gamepad-line", label: "趣味", value: character.hobby }] : []),
                  ...(gpsGranted
                    ? [{ icon: "ri-map-pin-line", label: "エリア", value: gpsArea || character.area }]
                    : []),
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-2.5 md:p-3">
                    <div className="flex items-center gap-1 text-pink-400 text-xs mb-1">
                      <i className={item.icon}></i>
                      <span>{item.label}</span>
                    </div>
                    <p className="text-xs md:text-sm font-medium text-gray-700 truncate">
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>

              {/* Description */}
              <div className="mb-4 md:mb-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1">
                  <i className="ri-chat-heart-line text-pink-400"></i>自己紹介
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">{character.description}</p>
              </div>

              {/* Tags */}
              <div className="mb-5 md:mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1">
                  <i className="ri-price-tag-3-line text-pink-400"></i>タグ
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {character.tags.map((tag) => (
                    <span
                      key={tag}
                      className="bg-pink-50 text-pink-500 text-xs px-2.5 py-1 rounded-full border border-pink-200"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* CTA — desktop inline version */}
              <div className="relative mt-2 hidden md:block">
                <div
                  className="absolute inset-0 rounded-2xl bg-gradient-to-r from-pink-400 to-rose-500 opacity-30 animate-ping"
                  style={{ animationDuration: "2s" }}
                ></div>
                <a
                  href={character.link}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="relative block w-full py-5 bg-gradient-to-r from-pink-500 via-rose-500 to-pink-600 text-white rounded-2xl text-center font-black text-lg hover:from-pink-600 hover:via-rose-600 hover:to-pink-700 transition-all cursor-pointer overflow-hidden group"
                  style={{ boxShadow: "0 8px 30px rgba(244, 63, 94, 0.5)" }}
                >
                  <span className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300 rounded-2xl"></span>
                  <span className="relative flex items-center justify-center gap-2">
                    <i className="ri-heart-3-fill text-xl animate-pulse"></i>
                    <span>今すぐ会いに行く♡</span>
                    <i className="ri-arrow-right-line text-lg"></i>
                  </span>
                  <p className="relative text-pink-100 text-xs mt-1 font-normal">
                    タップして今すぐチェック！
                  </p>
                </a>
              </div>
              <Link
                to="/map"
                className="hidden md:block w-full py-3 mt-2.5 border-2 border-pink-300 text-pink-500 rounded-full text-center font-semibold text-sm hover:bg-pink-50 transition-all cursor-pointer"
              >
                <i className="ri-map-pin-line mr-1"></i>マップで位置を確認
              </Link>
            </div>
          </div>

          {/* Mobile sticky CTA — only visible on mobile, fixed at bottom */}
          <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 px-4 pb-4 pt-3" style={{ background: "linear-gradient(to top, rgba(255,255,255,1) 70%, rgba(255,255,255,0))" }}>
            <a
              href={character.link}
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center justify-center gap-2 w-full py-4 bg-gradient-to-r from-pink-500 via-rose-500 to-pink-600 text-white rounded-2xl font-black text-base cursor-pointer active:scale-95 transition-transform"
              style={{ boxShadow: "0 6px 24px rgba(244, 63, 94, 0.55)" }}
            >
              <i className="ri-heart-3-fill text-lg animate-pulse"></i>
              <span>今すぐ会いに行く♡</span>
              <i className="ri-arrow-right-line text-base"></i>
            </a>
            <Link
              to="/map"
              className="flex items-center justify-center gap-1 w-full py-2.5 mt-2 border-2 border-pink-300 text-pink-500 rounded-full font-semibold text-sm cursor-pointer"
            >
              <i className="ri-map-pin-line"></i>マップで位置を確認
            </Link>
          </div>

          {/* Spacer so sticky CTA doesn't cover content on mobile */}
          <div className="md:hidden h-28" />

          {/* Related Characters */}
          {related.length > 0 && (
            <div className="mt-10 md:mt-16">
              <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">
                他の女の子
              </h2>
              <div className="grid grid-cols-3 gap-2.5 md:gap-5">
                {related.map((char) => (
                  <div
                    key={char.id}
                    onClick={() => handleRelatedClick(char.id)}
                    className="group bg-white rounded-2xl overflow-hidden border border-pink-100 hover:border-pink-300 transition-all cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && handleRelatedClick(char.id)}
                  >
                    <div className="relative w-full overflow-hidden" style={{ paddingBottom: "120%" }}>
                      <img
                        src={char.images[0]}
                        alt={char.name}
                        className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-2">
                        <h3 className="font-bold text-white text-xs md:text-sm leading-tight">
                          {char.name}
                        </h3>
                        <p className="text-pink-200 text-xs">{char.age != null ? `${char.age}歳` : "ひみつ"}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProfilePage;
